<template>
  <div class="popup-container">
    <!-- 팝업 오버레이 및 모달들 -->
    <div
      v-for="popup in visiblePopups"
      :key="popup.id"
      class="popup-overlay"
      :class="getPopupPositionClass(popup)"
      @click.self="handlePopupBackgroundClick(popup)"
    >
      <div
        class="popup-modal"
        :class="[getPopupAnimationClass(popup)]"
        :style="getPopupStyle(popup)"
      >
        <!-- 팝업 컨텐츠 -->
        <div class="popup-content" @click="handlePopupClick(popup)">
          <!-- 이미지 팝업 -->
          <template v-if="popup.popupType === 'image' || popup.imageUrl">
            <img
              :src="getImageUrl(popup.imageUrl)"
              :alt="popup.title"
              class="popup-image"
              :style="{ cursor: popup.linkUrl ? 'pointer' : 'default' }"
            />
          </template>

          <!-- 텍스트/HTML 팝업 -->
          <template v-else-if="popup.popupType === 'text' || popup.popupType === 'html'">
            <div class="popup-text-content">
              <h3 v-if="popup.title" class="popup-title">{{ popup.title }}</h3>
              <div v-if="popup.description" class="popup-description" v-html="popup.description"></div>
            </div>
          </template>
        </div>

        <!-- 팝업 하단 버튼 영역 -->
        <div class="popup-footer">
          <div class="popup-close-options">
            <!-- 오늘 하루 보지 않기 -->
            <label v-if="popup.showTodayClose" class="popup-checkbox-label">
              <input type="checkbox" @change="closePopup(popup, 'today')" />
              <span>오늘 하루 보지 않기</span>
            </label>

            <!-- 더이상 보지 않기 -->
            <label v-if="popup.showNeverClose" class="popup-checkbox-label">
              <input type="checkbox" @change="closePopup(popup, 'never')" />
              <span>더이상 보지 않기</span>
            </label>
          </div>

          <!-- 닫기 버튼 -->
          <button class="popup-close-btn" @click="closePopup(popup, 'normal')">
            닫기
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { getBaseURL } from '@/utils/config';

export default {
  name: 'PopupDisplay',
  data() {
    return {
      popups: [],
      visiblePopups: [],
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1024
    };
  },
  computed: {
    baseURL() {
      return getBaseURL();
    },
    currentPage() {
      // 현재 페이지 경로에 따른 페이지 타입 반환
      const path = this.$route?.path || '/';

      if (path === '/' || path === '/home') {
        return 'main';
      } else if (path.startsWith('/products')) {
        return 'products';
      } else if (path.startsWith('/product/')) {
        return 'product-detail';
      } else if (path.startsWith('/category')) {
        return 'category';
      } else if (path.startsWith('/cart')) {
        return 'cart';
      } else if (path.startsWith('/mypage')) {
        return 'mypage';
      }

      return path;
    }
  },
  watch: {
    '$route.path': {
      handler() {
        // 페이지 변경 시 팝업 다시 로드
        this.loadPopups();
      },
      immediate: false
    }
  },
  methods: {
    async loadPopups() {
      try {
        const response = await axios.get(`${this.baseURL}/api/popups`, {
          params: {
            page: this.currentPage,
            device: this.isMobile() ? 'mobile' : 'pc'
          }
        });

        this.popups = response.data || [];
        console.log('팝업 로드 완료:', this.popups.length, '개 (페이지:', this.currentPage, ')');

        // 닫은 팝업 필터링 후 표시
        this.showPopups();
      } catch (error) {
        console.error('팝업 로드 실패:', error);
        this.popups = [];
      }
    },

    isMobile() {
      return this.windowWidth <= 768;
    },

    showPopups() {
      // 쿠키에서 닫은 팝업 ID 목록 가져오기
      const closedToday = this.getClosedPopupIds('popup_closed_today');
      const closedNever = this.getClosedPopupIds('popup_closed_never');

      // 표시할 팝업 필터링
      this.visiblePopups = this.popups.filter(popup => {
        const popupId = popup.id.toString();
        // 오늘 닫은 팝업이나 더이상 보지 않기 한 팝업은 제외
        return !closedToday.includes(popupId) && !closedNever.includes(popupId);
      });

      console.log('표시할 팝업:', this.visiblePopups.length, '개');
    },

    getClosedPopupIds(cookieName) {
      const cookie = document.cookie
        .split('; ')
        .find(row => row.startsWith(cookieName + '='));
      if (cookie) {
        try {
          return JSON.parse(decodeURIComponent(cookie.split('=')[1]));
        } catch (e) {
          return [];
        }
      }
      return [];
    },

    setClosedPopupCookie(cookieName, popupId, days) {
      let closedIds = this.getClosedPopupIds(cookieName);
      if (!closedIds.includes(popupId.toString())) {
        closedIds.push(popupId.toString());
      }

      const expires = new Date();
      expires.setDate(expires.getDate() + days);
      document.cookie = `${cookieName}=${encodeURIComponent(JSON.stringify(closedIds))}; expires=${expires.toUTCString()}; path=/`;
    },

    closePopup(popup, closeType = 'normal') {
      // 팝업 닫기 API 호출
      axios.post(`${this.baseURL}/api/popups/${popup.id}/close`, null, {
        params: { closeType }
      }).catch(error => {
        console.error('팝업 닫기 기록 실패:', error);
      });

      // 쿠키 설정
      if (closeType === 'today') {
        this.setClosedPopupCookie('popup_closed_today', popup.id, 1);
      } else if (closeType === 'never') {
        this.setClosedPopupCookie('popup_closed_never', popup.id, 365);
      }

      // 표시 목록에서 제거
      this.visiblePopups = this.visiblePopups.filter(p => p.id !== popup.id);
    },

    handlePopupClick(popup) {
      // 팝업 클릭 기록
      axios.post(`${this.baseURL}/api/popups/${popup.id}/click`).catch(error => {
        console.error('팝업 클릭 기록 실패:', error);
      });

      // 링크 이동
      if (popup.linkUrl) {
        if (popup.openNewTab) {
          window.open(popup.linkUrl, '_blank');
        } else if (popup.linkUrl.startsWith('http')) {
          window.open(popup.linkUrl, '_blank');
        } else {
          this.$router.push(popup.linkUrl);
        }
      }
    },

    handlePopupBackgroundClick(popup) {
      if (popup.closeOnBackgroundClick) {
        this.closePopup(popup, 'normal');
      }
    },

    getImageUrl(imageUrl) {
      if (!imageUrl) return '';
      if (imageUrl.startsWith('http')) return imageUrl;
      return `${this.baseURL}${imageUrl}`;
    },

    getPopupStyle(popup) {
      const width = this.isMobile() && popup.useMobileSize ? popup.mobileWidth : popup.width;

      let style = {
        width: width + 'px',
        maxWidth: '95vw',
        borderRadius: (popup.borderRadius || 8) + 'px',
        zIndex: popup.zIndex || 10000,
        overflow: 'hidden'
      };

      if (popup.useShadow !== false) {
        style.boxShadow = '0 10px 40px rgba(0,0,0,0.3)';
      }

      return style;
    },

    getPopupPositionClass(popup) {
      const positions = {
        'center': 'popup-position-center',
        'top-left': 'popup-position-top-left',
        'top-right': 'popup-position-top-right',
        'bottom-left': 'popup-position-bottom-left',
        'bottom-right': 'popup-position-bottom-right',
        'full-screen': 'popup-position-full-screen'
      };
      return positions[popup.position] || 'popup-position-center';
    },

    getPopupAnimationClass(popup) {
      const animations = {
        'fade': 'popup-anim-fade',
        'slide-up': 'popup-anim-slide-up',
        'slide-down': 'popup-anim-slide-down',
        'zoom': 'popup-anim-zoom'
      };
      return animations[popup.animation] || 'popup-anim-fade';
    },

    handleResize() {
      this.windowWidth = window.innerWidth;
    }
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);

    // 약간의 딜레이 후 팝업 로드 (페이지 렌더링 후)
    setTimeout(() => {
      this.loadPopups();
    }, 500);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }
};
</script>

<style scoped>
/* 팝업 오버레이 */
.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 10000;
  display: flex;
  padding: 20px;
}

/* 위치별 스타일 */
.popup-position-center {
  justify-content: center;
  align-items: center;
}

.popup-position-top-left {
  justify-content: flex-start;
  align-items: flex-start;
}

.popup-position-top-right {
  justify-content: flex-end;
  align-items: flex-start;
}

.popup-position-bottom-left {
  justify-content: flex-start;
  align-items: flex-end;
}

.popup-position-bottom-right {
  justify-content: flex-end;
  align-items: flex-end;
}

.popup-position-full-screen {
  padding: 0;
}

.popup-position-full-screen .popup-modal {
  width: 100% !important;
  max-width: 100% !important;
  height: 100vh;
  border-radius: 0 !important;
}

/* 팝업 모달 */
.popup-modal {
  background-color: white;
  position: relative;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* 애니메이션 */
.popup-anim-fade {
  animation: popupFadeIn 0.3s ease-out;
}

.popup-anim-slide-up {
  animation: popupSlideUp 0.4s ease-out;
}

.popup-anim-slide-down {
  animation: popupSlideDown 0.4s ease-out;
}

.popup-anim-zoom {
  animation: popupZoom 0.3s ease-out;
}

@keyframes popupFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes popupSlideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes popupSlideDown {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes popupZoom {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* 팝업 컨텐츠 */
.popup-content {
  flex: 1;
  overflow: auto;
}

.popup-image {
  width: 100%;
  height: auto;
  display: block;
}

.popup-text-content {
  padding: 20px;
}

.popup-title {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 12px;
  color: #333;
}

.popup-description {
  font-size: 14px;
  line-height: 1.6;
  color: #666;
}

/* 팝업 하단 영역 */
.popup-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background-color: #f5f5f5;
  border-top: 1px solid #e0e0e0;
}

.popup-close-options {
  display: flex;
  gap: 16px;
}

.popup-checkbox-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #666;
  cursor: pointer;
}

.popup-checkbox-label input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.popup-close-btn {
  padding: 8px 20px;
  background-color: #333;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.popup-close-btn:hover {
  background-color: #555;
}

/* 반응형 */
@media (max-width: 768px) {
  .popup-overlay {
    padding: 10px;
  }

  .popup-footer {
    flex-direction: column;
    gap: 12px;
  }

  .popup-close-options {
    flex-direction: column;
    gap: 8px;
  }

  .popup-checkbox-label {
    font-size: 12px;
  }

  .popup-close-btn {
    width: 100%;
    padding: 12px;
  }
}
</style>
